import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, Briefcase, FileText, CheckCircle, DollarSign, MessageSquare } from "lucide-react";
import ChatBox from "@/components/ChatBox";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES_ICON = {
  "Programación": "💻", "Diseño": "🎨", "Redacción": "✍️",
  "Marketing": "📢", "Video & Audio": "🎬", "IA & Datos": "🤖",
};

const STATUS_LABEL = {
  open: "Abierto", in_progress: "En curso", completed: "Completado", closed: "Cerrado",
};
const STATUS_COLOR = {
  open: "text-primary bg-primary/10",
  in_progress: "text-amber-600 bg-amber-500/10",
  completed: "text-emerald-600 bg-emerald-500/10",
  closed: "text-muted-foreground bg-muted",
};

const PROP_STATUS_LABEL = {
  pending: "Pendiente", accepted: "Aceptada", rejected: "Rechazada",
};
const PROP_STATUS_COLOR = {
  pending: "text-amber-600 bg-amber-500/10",
  accepted: "text-emerald-600 bg-emerald-500/10",
  rejected: "text-destructive bg-destructive/10",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

/**
 * DashboardStatCards — Clickable stat cards.
 * Each card opens a dialog listing the items and letting the user click to manage.
 */
export default function DashboardStatCards({ stats }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openSection, setOpenSection] = useState(null); // "projects" | "proposals" | "accepted" | "earnings"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeProject, setActiveProject] = useState(null); // For inline chat view
  const [unread, setUnread] = useState({ total: 0, by_project: {}, owner_total: 0, freelancer_total: 0 });

  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/messages/unread-summary");
      setUnread(data || { total: 0, by_project: {}, owner_total: 0, freelancer_total: 0 });
    } catch { /* noop */ }
  };

  useEffect(() => { fetchUnread(); }, []);

  const loadItems = async (section) => {
    setLoading(true);
    setItems([]);
    try {
      if (section === "projects") {
        const { data } = await api.get("/projects/mine");
        setItems(data);
      } else if (section === "proposals") {
        const { data } = await api.get("/proposals/mine");
        setItems(data);
      } else if (section === "accepted") {
        const { data } = await api.get("/proposals/mine");
        setItems(data.filter((p) => p.status === "accepted"));
      } else if (section === "earnings") {
        const { data } = await api.get("/projects/assigned");
        setItems(data.filter((p) => p.status === "completed"));
      }
    } catch (e) {
      toast.error("Error al cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  const openSectionDialog = (section) => {
    setOpenSection(section);
    setActiveProject(null);
    loadItems(section);
  };

  const goToProject = (project_id) => {
    setOpenSection(null);
    setActiveProject(null);
    navigate(`/projects/${project_id}`);
  };

  // For "accepted" section: open chat inline instead of navigating.
  const openChatInline = (proposalItem) => {
    if (!proposalItem?.project?.project_id) return;
    setActiveProject(proposalItem.project);
  };

  // When user opens a chat inline, mark as read (best-effort).
  const markProjectRead = async (pid) => {
    try {
      await api.post(`/messages/${pid}/read`);
      // Optimistically clear unread for that project
      setUnread((u) => {
        const bp = { ...u.by_project };
        const cleared = bp[pid] || 0;
        delete bp[pid];
        return {
          ...u,
          by_project: bp,
          total: Math.max(0, (u.total || 0) - cleared),
          owner_total: Math.max(0, (u.owner_total || 0) - cleared),
          freelancer_total: Math.max(0, (u.freelancer_total || 0) - cleared),
        };
      });
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (activeProject?.project_id) markProjectRead(activeProject.project_id);
    // eslint-disable-next-line
  }, [activeProject?.project_id]);

  const sectionMeta = {
    projects: {
      title: "Mis proyectos",
      description: "Proyectos que publicaste. Clic en uno para gestionarlo, ver propuestas y aceptar freelancers.",
      empty: "Aún no has publicado ningún proyecto.",
    },
    proposals: {
      title: "Mis propuestas",
      description: "Propuestas que enviaste a proyectos. Clic para ver el proyecto y chatear.",
      empty: "Aún no has enviado propuestas.",
    },
    accepted: {
      title: "Propuestas aceptadas",
      description: "Proyectos donde fuiste seleccionado. Clic para abrir el chat y gestionar entregas.",
      empty: "Aún no tienes propuestas aceptadas.",
    },
    earnings: {
      title: "Proyectos completados (ganancias)",
      description: "Proyectos completados como freelancer.",
      empty: "Aún no tienes proyectos completados.",
    },
  };

  const cards = [
    { key: "projects", icon: Briefcase, label: "Mis proyectos", value: stats.my_projects, testid: "stat-my-projects", unread: unread.owner_total },
    { key: "proposals", icon: FileText, label: "Mis propuestas", value: stats.my_proposals, testid: "stat-my-proposals", unread: 0 },
    { key: "accepted", icon: CheckCircle, label: "Aceptadas", value: stats.accepted_proposals, testid: "stat-accepted", unread: unread.freelancer_total },
    { key: "earnings", icon: DollarSign, label: "Ganancias", value: `$${(stats.earnings || 0).toFixed(0)}`, testid: "stat-earnings", unread: 0 },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => openSectionDialog(c.key)}
            className="text-left group focus:outline-none focus:ring-2 focus:ring-primary/60 rounded-lg relative"
            data-testid={`${c.testid}-btn`}
          >
            {c.unread > 0 && (
              <span
                className="absolute -top-1 -right-1 z-10 min-w-[22px] h-[22px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold flex items-center justify-center shadow-md ring-2 ring-background"
                data-testid={`${c.testid}-unread-badge`}
                aria-label={`${c.unread} mensajes sin leer`}
              >
                {c.unread > 99 ? "99+" : c.unread}
              </span>
            )}
            <Card className={`border-border group-hover:border-primary/60 group-hover:shadow-md transition-all h-full ${c.unread > 0 ? "ring-1 ring-destructive/30" : ""}`}>
              <CardContent className="p-6 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-3">
                  <c.icon className="w-5 h-5 text-primary" />
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold tracking-tight">{c.value}</div>
                  <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">{c.label}</div>
                </div>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      <Dialog open={!!openSection} onOpenChange={(o) => { if (!o) { setOpenSection(null); setActiveProject(null); } }}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid={`dashboard-list-dialog-${openSection || ""}`}
        >
          {activeProject ? (
            /* Chat view for accepted proposal */
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveProject(null)}
                    className="text-muted-foreground hover:text-foreground p-1 -ml-1 rounded-md"
                    data-testid="dashboard-chat-back"
                    aria-label="Volver"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <DialogTitle className="truncate">{activeProject.title}</DialogTitle>
                </div>
                <DialogDescription>
                  <span className="inline-flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    Chat con el cliente · Adjunta archivos hasta 10MB
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-hidden -mx-6 px-6 pt-2">
                <ChatBox projectId={activeProject.project_id} currentUserId={user?.user_id} />
              </div>
              <div className="pt-2 border-t">
                <button
                  type="button"
                  onClick={() => goToProject(activeProject.project_id)}
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="dashboard-chat-open-project"
                >
                  Abrir proyecto completo <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{openSection && sectionMeta[openSection].title}</DialogTitle>
                <DialogDescription>
                  {openSection && sectionMeta[openSection].description}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-1 -mr-1">
                {loading ? (
                  <div className="text-center text-muted-foreground text-sm py-10">Cargando…</div>
                ) : items.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-10">
                    {openSection && sectionMeta[openSection].empty}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item) => {
                      const isProposalList = openSection === "proposals" || openSection === "accepted";
                      const project = isProposalList ? item.project : item;
                      const category = project?.category;
                      // Owner viewing "Mis proyectos": if there's an accepted freelancer, chat inline
                      const ownerHasFreelancer =
                        openSection === "projects" && !!project?.accepted_freelancer_id;
                      const canOpenChatInline =
                        openSection === "accepted" || ownerHasFreelancer;
                      const onClick = () => {
                        if (!project?.project_id) return;
                        if (canOpenChatInline) setActiveProject(project);
                        else goToProject(project.project_id);
                      };
                      return (
                        <button
                          key={item.proposal_id || item.project_id}
                          type="button"
                          onClick={onClick}
                          disabled={!project?.project_id}
                          className="w-full text-left p-3 rounded-md border hover:border-primary/60 hover:bg-muted/40 transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/60 disabled:opacity-50"
                          data-testid={`dashboard-list-item-${item.proposal_id || item.project_id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {category && (
                                  <span className="text-lg leading-none">{CATEGORIES_ICON[category] || "📁"}</span>
                                )}
                                <div className="font-medium truncate">
                                  {project?.title || <span className="italic text-muted-foreground">Proyecto eliminado</span>}
                                </div>
                              </div>
                              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                                {category && <span>{category}</span>}
                                {project?.budget != null && <span>Presupuesto ${project.budget.toFixed(0)}</span>}
                                {project?.company_name && <span>Cliente: {project.company_name}</span>}
                                {isProposalList && (
                                  <span>Enviada {fmtDate(item.created_at)} · Ofertaste ${item.amount?.toFixed(0)}</span>
                                )}
                                {openSection === "earnings" && project?.accepted_amount != null && (
                                  <span className="text-emerald-600 font-medium">Cobrado ${project.accepted_amount.toFixed(0)}</span>
                                )}
                                {ownerHasFreelancer && project.accepted_freelancer?.name && (
                                  <span className="text-primary font-medium">Freelancer: {project.accepted_freelancer.name}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 whitespace-nowrap">
                              {(() => {
                                const u = project?.project_id ? unread.by_project[project.project_id] || 0 : 0;
                                return u > 0 ? (
                                  <span
                                    className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold flex items-center justify-center shadow-sm"
                                    data-testid={`dashboard-item-unread-${project.project_id}`}
                                    aria-label={`${u} mensajes sin leer`}
                                  >
                                    {u > 99 ? "99+" : u}
                                  </span>
                                ) : null;
                              })()}
                              {isProposalList ? (
                                <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-sm ${PROP_STATUS_COLOR[item.status] || "bg-muted"}`}>
                                  {PROP_STATUS_LABEL[item.status] || item.status}
                                </span>
                              ) : (
                                <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-sm ${STATUS_COLOR[project?.status] || "bg-muted"}`}>
                                  {STATUS_LABEL[project?.status] || project?.status}
                                </span>
                              )}
                              {openSection === "projects" && project?.proposals_count != null && (
                                <span className="text-xs text-muted-foreground">
                                  {project.proposals_count} propuesta{project.proposals_count !== 1 ? "s" : ""}
                                </span>
                              )}
                              {canOpenChatInline ? (
                                <MessageSquare className="w-4 h-4 text-primary group-hover:scale-110 transition-transform mt-1" data-testid={`chat-icon-${project?.project_id || ""}`} />
                              ) : (
                                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-1" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
